// Оболочка И-1.1: конфиг → панель вкладок → экраны W1. Маршруты (memory-router, src/nav.ts):
//   /tab/:key            — вкладка сервера: matches → матч-центр (HQ-269); leagues, news, highlights, favorites — свои
//                          экраны W2–W3 (лиги, хаб новостей, ролики, избранное)
//   /match/:id           — экран матча (HQ-270)
//   /menu                — меню (HQ-271)
//   /menu/notifications  — «Уведомления» W4 (HQ-281): настройки сообщений бота; кнопка бота startapp=settings_push
//   /league/:id, /world-cup, /team/:id, /player/:id, /search, /news/:id, /highlight/:id — экраны W2–W3: турнир, чемпионат
//                          мира, команда, игрок, поиск, статья, просмотр ролика (свой модуль у каждого)
//   /soon/:kind/:id?     — старые адреса «Скоро»: перенаправление на новый адрес экрана
// Состояния до панели: нет initData (открыто в обычном браузере) — «Откройте в Telegram»; конфиг грузится — скелетон
// (без сети — «Нет интернета»); токен приложения не принят — «Приложение недоступно»; 5xx/429 — «Что-то пошло не так».
// Исключение рендера оболочки — экран ошибки (ui/ErrorBoundary); не загрузился кусок экрана после выката — одна
// перезагрузка на версию сборки (ui/chunkReload, HQ-883).
// Ссылка запуска (startapp) открывается поверх вкладки по умолчанию: BackButton ведёт в неё.
// Предложение «Добавить на главный экран» (HQ-772, src/homescreen) — лист на корне вкладки, не в сессии по ссылке.
import { type ComponentType, Suspense, useCallback, useEffect, useRef } from 'react';
import { createMemoryRouter, Navigate, Outlet, RouterProvider, useLocation, useNavigate, useParams, useRouteError, useSearchParams } from 'react-router-dom';
import { trackScreen } from './analytics/analytics';
import { tabScreen } from './analytics/events';
import { track } from './analytics/track';
import { resolveTabs, SCREEN_TABS, useConfig } from './api/config';
import { routeFromStartParam } from './deeplink';
import { HomeScreenPrompt } from './homescreen/HomeScreenPrompt';
import { t } from './i18n/i18n';
import { bindStrings, lazy } from './i18n/lazyScreen';
import { initialEntries, paths, tabOfPath, telegramLink } from './nav';
import { bindBackButton, isMock, isTelegram, startParam } from './telegram/webapp';
import { AppBar } from './ui/AppBar';
import { ErrorBoundary } from './ui/ErrorBoundary';
import { setHeaderState } from './ui/headerState';
import { OfflineBanner } from './ui/OfflineBanner';
import { PullToRefresh } from './ui/PullToRefresh';
import { ErrorView, Loading, StateView } from './ui/States';
import { TabBar } from './ui/TabBar';
import { ToastHost } from './ui/Toast';
import MatchCenterScreen from './screens/matchCenter/MatchCenterScreen';
import { SOON_TABS } from './screens/soon/soonTabs';
import { tabFallbackLabel } from './screens/soon/tabLabels';
import styles from './App.module.css';

// первый экран (матч-центр) — в основном куске; остальные экраны — отдельными кусками (lazy из i18n/lazyScreen: с куском
// экрана W2–W3 грузятся строки его области — таблица SCREEN_STRINGS ниже)
const MatchScreen = lazy(() => import('./screens/match/MatchScreen'));
const MenuScreen = lazy(() => import('./screens/menu/MenuScreen'));
const SoonScreen = lazy(() => import('./screens/soon/SoonScreen'));
const TournamentScreen = lazy(() => import('./screens/tournament/index'));
const WorldCupScreen = lazy(() => import('./screens/worldCup/index'));
const TeamScreen = lazy(() => import('./screens/team/index'));
const PlayerScreen = lazy(() => import('./screens/player/index'));
const SearchScreen = lazy(() => import('./screens/search/index'));
const ArticleScreen = lazy(() => import('./screens/news/ArticleRoute'));
const HighlightViewerScreen = lazy(() => import('./screens/highlights/ViewerRoute'));
const NotificationsScreen = lazy(() => import('./screens/notifications/index'));

/** Экраны вкладок панели (ключи — SCREEN_TABS). */
const TAB_SCREENS: Record<string, ComponentType> = {
  matches: MatchCenterScreen,
  leagues: lazy(() => import('./screens/leagues/index')),
  news: lazy(() => import('./screens/news/index')),
  highlights: lazy(() => import('./screens/highlights/index')),
  favorites: lazy(() => import('./screens/favorites/index')),
};

/** Экран W2–W4 → область строк extra/<область>.json: грузится вместе с куском экрана, до его первого рендера (HQ-667).
 *  Строки экрана матча, меню, «Скоро» и оболочки — в основном куске (i18n/extraFiles.ts, FIRST_SCREEN_AREAS). */
const SCREEN_STRINGS = [
  [TournamentScreen, 'tournament'], [WorldCupScreen, 'worldCup'], [TeamScreen, 'team'], [PlayerScreen, 'player'],
  [SearchScreen, 'search'], [ArticleScreen, 'news'], [HighlightViewerScreen, 'highlights'],
  [TAB_SCREENS.leagues!, 'leagues'], [TAB_SCREENS.news!, 'news'], [TAB_SCREENS.highlights!, 'highlights'], [TAB_SCREENS.favorites!, 'favorites'],
  [NotificationsScreen, 'notifications'],
] as const;
bindStrings(SCREEN_STRINGS);

const isSoonTab = (pathname: string) => pathname.startsWith('/tab/') && SOON_TABS.has(tabOfPath(pathname) ?? '');

function useTabs() {
  const config = useConfig();
  return { config, ...resolveTabs(config.data, tabFallbackLabel) };
}

function Shell() {
  const { config, items, defaultKey } = useTabs();
  const location = useLocation();
  const navigate = useNavigate();
  // вкладка-заглушка «Скоро» (W2–W3, src/screens/soon/soonTabs.ts) — не корень: поверх вкладки, где были, как в W1
  const onSoonTab = isSoonTab(location.pathname);
  const isTabRoot = (location.pathname.startsWith('/tab/') && !onSoonTab) || location.pathname === '/';
  const lastTab = useRef(defaultKey);
  lastTab.current = tabOfPath(location.pathname) ?? (location.pathname === '/' ? defaultKey : lastTab.current);
  const activeKey = lastTab.current;

  const back = useCallback(() => navigate(-1), [navigate]);
  useEffect(() => bindBackButton(!isTabRoot, back), [isTabRoot, back]);
  // без панели (пустой tab_bar) — без её высоты в отступах
  useEffect(() => { document.documentElement.toggleAttribute('data-no-tabbar', items.length === 0); }, [items.length]);

  if (config.isPending) return <Loading query={config} />;
  if (config.isError) return <ErrorView error={config.error} onRetry={() => void config.refetch()} />;
  const onSelect = (key: string) => {
    track('tab_select', { tab: tabScreen(key) });
    // вкладка-заглушка — «Скоро» поверх: «Назад» возвращает на вкладку, где были
    navigate(paths.tab(key), { replace: SOON_TABS.has(key) ? onSoonTab : isTabRoot || onSoonTab });
  };
  return (
    <div className={styles.shell}>
      <div className={styles.topScrim} aria-hidden="true" />
      {isMock && !isTabRoot && <button className={styles.devBack} onClick={back}>‹ {t('shell.back')} (dev)</button>}
      {isTabRoot && <AppBar title={items.find((i) => i.key === activeKey)?.label ?? tabFallbackLabel(activeKey)}
        header={config.data?.navigation?.headers?.[activeKey]} />}
      <main className={styles.content}><Suspense fallback={<Loading />}><Outlet /></Suspense></main>
      <PullToRefresh />
      <OfflineBanner />
      <ToastHost />
      {/* ярлык на главном экране: лист — только на корне вкладки (не поверх матча, меню, «Скоро») и не после ссылки */}
      <HomeScreenPrompt onTab={isTabRoot} fromLink={Boolean(deepLink)} />
      {items.length > 0 && <TabBar items={items} active={activeKey} onSelect={onSelect} />}
    </div>
  );
}

function TabRoute() {
  const { key } = useParams();
  const [query] = useSearchParams();
  const { items, defaultKey } = useTabs();
  const live = query.get('live') === '1';
  useEffect(() => { if (live) setHeaderState({ liveOnly: true }); }, [live]);
  // панели нет (пустой tab_bar) — открыт матч-центр
  if (!items.length) return key === 'matches' ? <MatchCenterScreen /> : <Navigate to={paths.tab('matches')} replace />;
  const item = items.find((i) => i.key === key);
  // незнакомая вкладка (startapp=tab_xyz, ключа нет в панели) — вкладка по умолчанию
  const Screen = SCREEN_TABS.has(item?.key ?? '') ? TAB_SCREENS[item!.key] : undefined;
  if (!Screen) return <Navigate to={paths.tab(defaultKey)} replace />;
  return <Screen />;
}

function IndexRoute() {
  const { items, defaultKey } = useTabs();
  const key = items.length ? defaultKey : 'matches';
  return <Navigate to={paths.tab(key)} replace />;
}

function RouteError() {
  const error = useRouteError();
  useEffect(() => { console.error(error); }, [error]);
  return <ErrorView error={error} onRetry={() => window.location.reload()} />;
}

/** Открыто вне Telegram (нет initData): ссылка на Mini App в боте. */
function OpenInTelegram() {
  const bot = import.meta.env.VITE_BOT_USERNAME ?? '';
  return (
    <StateView art="info" title={t('shell.telegram.title')} text={t('shell.telegram.text')}>
      <a className={styles.link} href={telegramLink(bot, import.meta.env.VITE_MINI_APP_SHORT_NAME ?? '')}>
        {bot ? t('shell.telegram.button', bot) : t('shell.telegram.title')}
      </a>
    </StateView>
  );
}

const ROUTES = [
  { path: '/', element: <IndexRoute /> },
  { path: '/tab/:key', element: <TabRoute /> },
  { path: '/match/:id', element: <MatchScreen /> },
  { path: '/menu', element: <MenuScreen /> },
  { path: '/menu/notifications', element: <NotificationsScreen /> },
  { path: '/league/:id', element: <TournamentScreen /> },
  { path: '/world-cup', element: <WorldCupScreen /> },
  { path: '/team/:id', element: <TeamScreen /> },
  { path: '/player/:id', element: <PlayerScreen /> },
  { path: '/search', element: <SearchScreen /> },
  { path: '/news/:id', element: <ArticleScreen /> },
  { path: '/highlight/:id', element: <HighlightViewerScreen /> },
  { path: '/soon/:kind/:id?', element: <SoonScreen /> },
  { path: '*', element: <IndexRoute /> },
];

// dev: ?route=/team/33 — открыть любой экран сразу (обход вёрстки скриптами); в сборку прода не попадает
const devRoute = import.meta.env.DEV ? new URLSearchParams(window.location.search).get('route') : null;
const deepLink = devRoute ?? routeFromStartParam(startParam());
const router = createMemoryRouter([
  // Упал экран — наш экран ошибки на месте контента (вкладки живы), а не трассировка react-router. Упала сама оболочка
  // (панель вкладок, шапка, лист ярлыка) — экран ошибки на весь экран (HQ-883), а не белый экран.
  { element: <ErrorBoundary><Shell /></ErrorBoundary>, children: [{ errorElement: <RouteError />, children: ROUTES }] },
], initialEntries(deepLink));

// экраны — в аналитику: первый и каждая смена маршрута. События ждут в очереди track.ts и уходят после tg_launch.
// Первый экран холодного старта по ссылке — источник link.
trackScreen(router.state.location, deepLink ? 'link' : undefined);
router.subscribe((state) => trackScreen(state.location));

export function App() {
  if (!isTelegram && !isMock) return <OpenInTelegram />;
  return <ErrorBoundary><RouterProvider router={router} /></ErrorBoundary>;
}
